// src/business/pages/procurement/RFQManagement.jsx
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Search, Send, Eye, CheckCircle, ExternalLink, X, ChevronDown, Building2, Mail, User } from "lucide-react";
import Swal from "../../../utils/apparifySwal";
import { procurementAPI, suppliersAPI } from "../../services/businessErpService";
import { PageHeader, DataTable, Pagination, StatusBadge, FormModal, BtnPrimary, BtnSecondary } from "../../components/erp";
import Panel from "../../components/Panel";
import { fmtPeso } from "../../../utils/formatters";
import { getPRLabel, getRFQLabel } from "../../../utils/documentLabels";
import ShopSelect from "../../../components/ShopSelect";
import DatePickerInput from "../../../components/DatePickerInput";
import { businessSwalBase } from "../../utils/businessSwal";

const inputCls =
  "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-neutral-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100";

const COLUMNS = [
  { key: "id", label: "RFQ #", sortable: true, className: "font-mono text-xs", render: (r) => getRFQLabel(r) },
  { key: "title", label: "Title", sortable: true, render: (r) => getRFQDisplayTitle(r) },
  { key: "product_name", label: "Product", render: (r) => r.product_name || "—" },
  { key: "rfq_type", label: "Type", render: (r) => <StatusBadge status={r.rfq_type || "STANDARD"} /> },
  { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  { key: "quote_count", label: "Quotes", render: (r) => r.quote_count ?? 0 },
  {
    key: "deadline_at", label: "Deadline", sortable: true,
    render: (r) => r.deadline_at ? new Date(r.deadline_at).toLocaleDateString() : "—",
  },
  { key: "actions", label: "", render: (r) => r._actions },
];

const MOJIBAKE_MARKERS = ["Ã", "Â", "â"];
const mojibakeDecoder = typeof TextDecoder !== "undefined" ? new TextDecoder("utf-8") : null;

function repairSuspiciousMojibake(value) {
  const input = typeof value === "string" ? value : "";
  if (!input || !mojibakeDecoder || !MOJIBAKE_MARKERS.some((marker) => input.includes(marker))) {
    return input;
  }

  try {
    const bytes = Uint8Array.from(input, (char) => char.charCodeAt(0) & 0xff);
    const repaired = mojibakeDecoder.decode(bytes);
    return repaired || input;
  } catch {
    return input;
  }
}

function normalizeDisplayText(value) {
  if (value == null) return "";
  return repairSuspiciousMojibake(String(value)).replace(/\r\n?/g, "\n");
}

function hasDisplayText(value) {
  return normalizeDisplayText(value).trim().length > 0;
}

function getDisplayText(value, fallback = "-") {
  const normalized = normalizeDisplayText(value).trim();
  return normalized || fallback;
}

function getRFQAutoTitleFromPR(pr) {
  return `RFQ for ${getDisplayText(pr?.product_name, "Product")}`;
}

function getRFQDisplayTitle(rfq) {
  const title = getDisplayText(rfq?.title, "Untitled RFQ");
  if (!rfq?.purchase_requisition_id) return title;
  return title.replace(/\s+[—-]\s+PR-\d+\s*$/u, "");
}

function getQuoteLines(quote) {
  return Array.isArray(quote?.lines) ? quote.lines : [];
}

function getQuoteLineCount(quote) {
  const explicitCount = Number(quote?.line_count ?? quote?.lineCount ?? 0);
  if (Number.isFinite(explicitCount) && explicitCount > 0) {
    return explicitCount;
  }
  return getQuoteLines(quote).length;
}

function hasVisibleQuoteLineBreakdown(quote) {
  const lineCount = getQuoteLineCount(quote);
  return lineCount === 0 || getQuoteLines(quote).length > 0;
}

function getQuoteDisplayQuantity(quote) {
  const lines = getQuoteLines(quote);
  if (lines.length) {
    return lines.reduce((sum, line) => sum + Number(line.quotedQuantity ?? line.quoted_quantity ?? 0), 0);
  }
  return Number(quote?.quantity || 0);
}

function getQuoteDisplayTotalCents(quote) {
  const lines = getQuoteLines(quote);
  if (lines.length) {
    return lines.reduce((sum, line) => sum + Number(line.lineTotalCents ?? line.line_total_cents ?? 0), 0);
  }
  return Number(quote?.unit_price_cents || 0) * Number(quote?.quantity || 0);
}

function getQuoteDisplayUnitPriceCents(quote) {
  const lines = getQuoteLines(quote);
  if (!lines.length) return Number(quote?.unit_price_cents || 0);

  const quantity = getQuoteDisplayQuantity(quote);
  if (!quantity) return Number(quote?.unit_price_cents || 0);
  return Math.round(getQuoteDisplayTotalCents(quote) / quantity);
}

function getLineLabel(line, fallbackPrefix = "Line") {
  const fallbackLineNumber = line?.line_no || line?.lineNo || "";
  const rawLabel =
    line?.variantTitle ||
    line?.variant_title ||
    line?.variantSku ||
    line?.variant_sku;

  if (hasDisplayText(rawLabel)) {
    return getDisplayText(rawLabel, fallbackPrefix);
  }

  return fallbackLineNumber ? `${fallbackPrefix} ${fallbackLineNumber}` : fallbackPrefix;
}

function getAvailabilityLabel(line) {
  const normalized = String(line?.availabilityStatus || line?.availability_status || "").trim();
  if (!normalized) return "-";
  return normalized
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getSafeAvailabilityLabel(line) {
  return getAvailabilityLabel(line);
}

const INVITEABLE_RFQ_STATUSES = ["SENT", "OPEN_FOR_QUOTES", "PARTIALLY_QUOTED", "QUOTES_RECEIVED"];

function canInviteMoreSuppliers(status) {
  return INVITEABLE_RFQ_STATUSES.includes(String(status || "").trim());
}

function NoteCard({ label, value, tone = "neutral", compact = false }) {
  if (!hasDisplayText(value)) return null;

  const tones = {
    neutral: "border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-900/40",
    accent: "border-sky-200 bg-sky-50 dark:border-sky-800/60 dark:bg-sky-950/30",
    subtle: "border-neutral-200 bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800/40",
  };

  return (
    <div className={`rounded-xl border px-3 py-2 ${tones[tone] || tones.neutral}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
        {label}
      </p>
      <div
        className={[
          "mt-1 whitespace-pre-wrap break-words text-neutral-700 dark:text-neutral-200",
          compact ? "text-xs leading-relaxed" : "text-sm leading-relaxed",
        ].join(" ")}
      >
        {normalizeDisplayText(value).trim()}
      </div>
    </div>
  );
}

export default function RFQManagement() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");
  const swalBase = useMemo(() => ({ ...businessSwalBase, target: "#business-swal-root" }), []);

  // Create / Send RFQ
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    purchaseRequisitionId: "", title: "", notes: "", deadlineAt: "",
    rfqType: "STANDARD", expectedDeliveryAt: "", currency: "PHP", termsAcknowledged: false,
  });
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [approvedPRs, setApprovedPRs] = useState([]);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);
  const supplierDropdownRef = useRef(null);

  // Invite supplier (legacy per-RFQ invite, still useful for post-creation invites)
  const [showInvite, setShowInvite] = useState(null);
  const [inviteSupplierId, setInviteSupplierId] = useState("");
  const [inviteReturnDetailId, setInviteReturnDetailId] = useState(null);

  // RFQ Detail (with quotes)
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchRFQs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await procurementAPI.listRFQs({ page, limit: 20, sort: sortField, dir: sortDir, search });
      setData(res?.data || res?.items || []);
      setTotalPages(res?.pagination?.totalPages || res?.meta?.totalPages || 1);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, sortField, sortDir, search]);

  useEffect(() => { fetchRFQs(); }, [fetchRFQs]);

  // Load approved PRs + suppliers when create modal opens
  useEffect(() => {
    if (!showCreate) return;
    procurementAPI.listPRs({ status: "APPROVED", limit: 100 })
      .then((res) => setApprovedPRs(res?.data || res?.items || []))
      .catch(() => setApprovedPRs([]));
    suppliersAPI.list({ limit: 500 })
      .then((res) => setAllSuppliers(res?.data || res?.items || []))
      .catch(() => setAllSuppliers([]));
  }, [showCreate]);

  // Load suppliers when invite modal opens (for legacy per-RFQ invite flow)
  useEffect(() => {
    if (!showInvite) return;
    if (allSuppliers.length) return; // already loaded
    suppliersAPI.list({ limit: 500 })
      .then((res) => setAllSuppliers(res?.data || res?.items || []))
      .catch(() => setAllSuppliers([]));
  }, [showInvite, allSuppliers.length]);

  // Close supplier dropdown when clicking outside
  useEffect(() => {
    if (!supplierDropdownOpen) return;
    const handler = (e) => {
      if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(e.target)) {
        setSupplierDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [supplierDropdownOpen]);

  // Filtered & sorted suppliers for selector
  const filteredSuppliers = useMemo(() => {
    let list = [...allSuppliers];
    if (supplierSearch.trim()) {
      const q = supplierSearch.toLowerCase();
      list = list.filter((s) =>
        (s.name || "").toLowerCase().includes(q) ||
        (s.contact_name || "").toLowerCase().includes(q) ||
        (s.contact_email || "").toLowerCase().includes(q)
      );
    }
    // Sort: ACTIVE first, then by name
    list.sort((a, b) => {
      const aActive = a.relationship_status === "ACTIVE" ? 0 : 1;
      const bActive = b.relationship_status === "ACTIVE" ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      return (a.name || "").localeCompare(b.name || "");
    });
    return list;
  }, [allSuppliers, supplierSearch]);

  const toggleSupplier = (supplierId) => {
    setSelectedSupplierIds((prev) =>
      prev.includes(supplierId) ? prev.filter((id) => id !== supplierId) : [...prev, supplierId]
    );
  };

  const removeSupplier = (supplierId) => {
    setSelectedSupplierIds((prev) => prev.filter((id) => id !== supplierId));
  };

  const selectedSupplierObjects = useMemo(
    () => allSuppliers.filter((s) => selectedSupplierIds.includes(s.id)),
    [allSuppliers, selectedSupplierIds],
  );

  const selectedApprovedPR = useMemo(
    () => approvedPRs.find((pr) => String(pr.id) === String(createForm.purchaseRequisitionId)) || null,
    [approvedPRs, createForm.purchaseRequisitionId],
  );

  const invitedSupplierIdsForCurrentInvite = useMemo(() => {
    if (!showInvite || !detail || detail.id !== showInvite || !Array.isArray(detail.suppliers)) {
      return [];
    }
    return detail.suppliers
      .map((supplier) => String(supplier?.supplier_id))
      .filter(Boolean);
  }, [detail, showInvite]);

  const availableInviteSuppliers = useMemo(() => {
    if (!invitedSupplierIdsForCurrentInvite.length) return allSuppliers;
    const invitedIds = new Set(invitedSupplierIdsForCurrentInvite);
    return allSuppliers.filter((supplier) => !invitedIds.has(String(supplier.id)));
  }, [allSuppliers, invitedSupplierIdsForCurrentInvite]);

  const openInviteModal = useCallback((rfqId, { returnToDetail = false } = {}) => {
    setInviteSupplierId("");
    setInviteReturnDetailId(returnToDetail ? rfqId : null);
    if (returnToDetail) {
      setDetail(null);
    }
    setShowInvite(rfqId);
  }, []);

  const handleCreate = async () => {
    if (!selectedSupplierIds.length) return;
    if (!createForm.termsAcknowledged) {
      alert("Acknowledge the RFQ submission terms before sending this request for quotation.");
      return;
    }

    // Friendly stale-state guard; backend remains the source of truth.
    if (!approvedPRs.some((pr) => String(pr.id) === String(createForm.purchaseRequisitionId))) {
      alert("Selected approved PR is no longer available. It may have already been used to send an RFQ.");
      return;
    }

    setSubmitting(true);
    try {
      await procurementAPI.createRFQ({
        purchaseRequisitionId: Number(createForm.purchaseRequisitionId),
        title: createForm.title,
        notes: createForm.notes,
        deadlineAt: createForm.deadlineAt || null,
        rfqType: createForm.rfqType,
        expectedDeliveryAt: createForm.expectedDeliveryAt || null,
        currency: createForm.currency,
        supplierIds: selectedSupplierIds,
        termsAcknowledged: true,
      });
      setShowCreate(false);
      setCreateForm({ purchaseRequisitionId: "", title: "", notes: "", deadlineAt: "", rfqType: "STANDARD", expectedDeliveryAt: "", currency: "PHP", termsAcknowledged: false });
      setSelectedSupplierIds([]);
      setSupplierSearch("");
      fetchRFQs();
    } catch (e) {
      alert(e?.message || "Failed to send RFQ");
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvite = async () => {
    const rfqId = showInvite;
    const shouldReturnToDetail = inviteReturnDetailId === rfqId;
    try {
      await procurementAPI.inviteSupplier(rfqId, Number(inviteSupplierId));
      setShowInvite(null);
      setInviteSupplierId("");
      setInviteReturnDetailId(null);
      if (shouldReturnToDetail) {
        viewDetail(rfqId);
      } else if (detail && detail.id === rfqId) {
        viewDetail(rfqId);
      }
      fetchRFQs();
    } catch (e) {
      alert(e?.message || "Failed to invite supplier");
    }
  };

  const viewDetail = async (rfqId) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await procurementAPI.getRFQ(rfqId);
      setDetail(res?.data || null);
    } catch {
      alert("Failed to load RFQ details");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeInviteModal = () => {
    const returnRfqId = inviteReturnDetailId;
    setShowInvite(null);
    setInviteSupplierId("");
    setInviteReturnDetailId(null);
    if (returnRfqId) {
      viewDetail(returnRfqId);
    }
  };

  const handleAcceptQuote = async (rfqId, quote) => {
    const lineCount = getQuoteLineCount(quote);
    if (lineCount > 0 && !hasVisibleQuoteLineBreakdown(quote)) {
      await Swal.fire({
        ...swalBase,
        icon: "warning",
        title: "Quote details unavailable",
        text: "This quote was submitted with line-level pricing, but the line breakdown failed to load. Refresh the RFQ details before accepting it.",
        confirmButtonText: "OK",
      });
      return;
    }

    const confirmation = await Swal.fire({
      ...swalBase,
      icon: "warning",
      title: "Accept supplier quote?",
      html: `
        <div class="space-y-3 text-left text-sm">
          <p>This will award the RFQ to the selected supplier and create a Purchase Order${lineCount > 0 ? ` from <strong>${lineCount} quote line item${lineCount === 1 ? "" : "s"}</strong>` : " from this quote"}.</p>
          <div class="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            Other submitted quotes for this RFQ will be rejected. Review the quantities and prices before continuing.
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Accept & Create PO",
      cancelButtonText: "Review first",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!confirmation.isConfirmed) return;
    try {
      await procurementAPI.acceptQuote(rfqId, quote.id);
      setDetail(null);
      fetchRFQs();
      await Swal.fire({
        ...swalBase,
        icon: "success",
        title: "Quote accepted",
        text: "The Purchase Order was created and sent for finance approval.",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (e) {
      await Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Quote acceptance failed",
        text: e?.message || "Failed to accept quote.",
        confirmButtonText: "OK",
      });
    }
  };

  const rows = data.map((r) => ({
    ...r,
    _actions: (
      <div className="flex gap-1">
        <button
          onClick={(e) => { e.stopPropagation(); viewDetail(r.id); }}
          title="View Details & Quotes"
          className="rounded-lg bg-neutral-100 p-1.5 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
        >
          <Eye className="h-3.5 w-3.5" />
        </button>
        {canInviteMoreSuppliers(r.status) && (
          <button
            onClick={(e) => { e.stopPropagation(); openInviteModal(r.id); }}
            title="Invite Supplier"
            className="rounded-lg bg-blue-50 p-1.5 text-blue-600 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-400 dark:hover:bg-blue-900"
          >
            <Send className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    ),
  }));

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Procurement" title="Request for Quotations" subtitle="Send RFQs to suppliers and compare their quotes">
        <BtnPrimary onClick={() => setShowCreate(true)}><Send className="h-4 w-4 -ml-1" /> Send RFQ</BtnPrimary>
      </PageHeader>

      <Panel
        title="RFQ Records"
        right={(
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
            <input
              type="text" placeholder="Search RFQs…" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full rounded-lg border border-neutral-200 bg-white py-1.5 pl-8 pr-3 text-xs outline-none focus:ring-2 focus:ring-neutral-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
            />
          </div>
        )}
      >
        <DataTable
          columns={COLUMNS} data={rows} loading={loading}
          emptyMessage="No RFQs created yet. Approve a PR first, then create an RFQ."
          sortField={sortField} sortDir={sortDir}
          onSort={(f, d) => { setSortField(f); setSortDir(d); setPage(1); }}
          onRowClick={(r) => viewDetail(r.id)}
        />
        {totalPages > 1 && <div className="mt-4"><Pagination page={page} totalPages={totalPages} onPageChange={setPage} /></div>}
      </Panel>

      {/* ── Send RFQ Modal ── */}
      <FormModal open={showCreate} onClose={() => setShowCreate(false)} title="Send Request for Quotation" size="lg"
        footer={
          <>
            <BtnSecondary onClick={() => setShowCreate(false)}>Cancel</BtnSecondary>
            <BtnPrimary onClick={handleCreate} disabled={submitting || !createForm.purchaseRequisitionId || !createForm.title || !selectedSupplierIds.length || !createForm.termsAcknowledged}>
              <Send className="h-4 w-4 -ml-0.5" />
              {submitting ? "Sending…" : `Send RFQ${selectedSupplierIds.length ? ` to ${selectedSupplierIds.length} supplier${selectedSupplierIds.length > 1 ? "s" : ""}` : ""}`}
            </BtnPrimary>
          </>
        }
      >
        <div className="space-y-4">
          {/* PR Selector */}
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Approved Purchase Requisition * (one-time use)</label>
            <ShopSelect
              value={createForm.purchaseRequisitionId}
              onValueChange={(value) => {
                const pr = approvedPRs.find((purchaseRequisition) => String(purchaseRequisition.id) === value);
                setCreateForm((f) => ({
                  ...f,
                  purchaseRequisitionId: value,
                  title: pr ? getRFQAutoTitleFromPR(pr) : f.title,
                }));
              }}
              options={[
                { value: "", label: "— Select an approved PR (consumed after send) —" },
                ...approvedPRs.map((pr) => ({ value: pr.id, label: `${getPRLabel(pr)} — ${pr.product_name || "Product"} — Qty: ${pr.requested_quantity}` })),
              ]}
              ariaLabel="Approved Purchase Requisition"
              align="start"
              minPanelWidth={320}
              triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm"
              panelClassName="py-1"
            />
            {selectedApprovedPR && (
              <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                Source PR: <span className="font-medium text-neutral-700 dark:text-neutral-200">{getPRLabel(selectedApprovedPR)}</span>
              </p>
            )}
            {approvedPRs.length === 0 && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">No approved PRs found. Create and approve a PR first.</p>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Title *</label>
            <input type="text" value={createForm.title}
              onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))}
              className={inputCls} placeholder="RFQ title (auto-filled from PR)" />
          </div>

          {/* ── Supplier Recipients ── */}
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Send to Suppliers *</label>

            {/* Selected supplier chips */}
            {selectedSupplierObjects.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {selectedSupplierObjects.map((s) => (
                  <span key={s.id} className="inline-flex items-center gap-1 rounded-lg bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/50 dark:text-blue-200">
                    <Building2 className="h-3 w-3" />
                    {s.name}
                    <button type="button" onClick={() => removeSupplier(s.id)} className="ml-0.5 rounded-full p-0.5 hover:bg-blue-200 dark:hover:bg-blue-800">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Searchable dropdown */}
            <div className="relative" ref={supplierDropdownRef}>
              <div
                className={inputCls + " flex cursor-pointer items-center gap-2"}
                onClick={() => setSupplierDropdownOpen((v) => !v)}
              >
                <Search className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
                <input
                  type="text"
                  value={supplierSearch}
                  onChange={(e) => { setSupplierSearch(e.target.value); setSupplierDropdownOpen(true); }}
                  onFocus={() => setSupplierDropdownOpen(true)}
                  placeholder={selectedSupplierIds.length ? "Search to add more…" : "Search suppliers by name, contact, or email…"}
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-neutral-400 dark:text-neutral-100"
                  onClick={(e) => e.stopPropagation()}
                />
                <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-neutral-400 transition-transform ${supplierDropdownOpen ? "rotate-180" : ""}`} />
              </div>

              {supplierDropdownOpen && (
                <div className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-neutral-200 bg-white shadow-lg dark:border-neutral-700 dark:bg-neutral-800">
                  {filteredSuppliers.length === 0 ? (
                    <div className="px-3 py-4 text-center text-xs text-neutral-500 dark:text-neutral-400">
                      {allSuppliers.length === 0 ? "No suppliers linked to this business yet." : "No suppliers match your search."}
                    </div>
                  ) : (
                    filteredSuppliers.map((s) => {
                      const isSelected = selectedSupplierIds.includes(s.id);
                      const isActive = s.relationship_status === "ACTIVE";
                      return (
                        <button
                          key={s.id}
                          type="button"
                          disabled={!isActive}
                          onClick={() => { toggleSupplier(s.id); }}
                          className={[
                            "flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors",
                            isSelected
                              ? "bg-blue-50 dark:bg-blue-950/40"
                              : "hover:bg-neutral-50 dark:hover:bg-neutral-700/50",
                            !isActive && "opacity-50 cursor-not-allowed",
                          ].filter(Boolean).join(" ")}
                        >
                          {/* Checkbox */}
                          <span className={[
                            "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px]",
                            isSelected
                              ? "border-blue-500 bg-blue-500 text-white"
                              : "border-neutral-300 dark:border-neutral-600",
                          ].join(" ")}>
                            {isSelected && "✓"}
                          </span>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{s.name}</span>
                              <span className={[
                                "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                                isActive
                                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300"
                                  : "bg-neutral-100 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400",
                              ].join(" ")}>
                                {s.relationship_status || s.status || "UNKNOWN"}
                              </span>
                            </div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-neutral-500 dark:text-neutral-400">
                              {s.contact_name && (
                                <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{s.contact_name}</span>
                              )}
                              {s.contact_email && (
                                <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{s.contact_email}</span>
                              )}
                              {s.product_count != null && (
                                <span>{s.product_count} product{s.product_count !== 1 ? "s" : ""} mapped</span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {allSuppliers.length > 0 && allSuppliers.every((s) => s.relationship_status !== "ACTIVE") && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">No active suppliers available. Suppliers must have an active relationship before they can receive RFQs.</p>
            )}
            {!selectedSupplierIds.length && (
              <p className="mt-1 text-[10px] text-neutral-400">Select at least one supplier to receive this RFQ.</p>
            )}
          </div>

          {/* Type + Currency row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">RFQ Type</label>
              <ShopSelect
                value={createForm.rfqType}
                onValueChange={(value) => setCreateForm((current) => ({ ...current, rfqType: value }))}
                options={[{ value: "STANDARD", label: "Standard" }, { value: "URGENT", label: "Urgent" }, { value: "BLANKET", label: "Blanket (recurring)" }]}
                ariaLabel="RFQ Type"
                align="start"
                minPanelWidth={200}
                triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm"
                panelClassName="py-1"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Currency</label>
              <ShopSelect
                value={createForm.currency}
                onValueChange={(value) => setCreateForm((current) => ({ ...current, currency: value }))}
                options={[{ value: "PHP", label: "PHP (₱)" }, { value: "USD", label: "USD ($)" }]}
                ariaLabel="Currency"
                align="start"
                minPanelWidth={180}
                triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm"
                panelClassName="py-1"
              />
            </div>
          </div>

          {/* Dates row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Quote Deadline</label>
              <DatePickerInput value={createForm.deadlineAt}
                onValueChange={(value) => setCreateForm((f) => ({ ...f, deadlineAt: value }))}
                title="SELECT QUOTE DEADLINE" ariaLabel="Quote deadline"
                className={inputCls} />
              <p className="mt-0.5 text-[10px] text-neutral-400">Last date suppliers can submit quotes</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Expected Delivery</label>
              <DatePickerInput value={createForm.expectedDeliveryAt}
                onValueChange={(value) => setCreateForm((f) => ({ ...f, expectedDeliveryAt: value }))}
                title="SELECT DELIVERY DATE" ariaLabel="Expected delivery"
                className={inputCls} />
              <p className="mt-0.5 text-[10px] text-neutral-400">When you need the goods delivered</p>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Notes for Suppliers</label>
            <textarea rows={3} value={createForm.notes}
              onChange={(e) => setCreateForm((f) => ({ ...f, notes: e.target.value }))}
              className={inputCls} placeholder="Special requirements, delivery instructions, quality specs…" />
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-200">
            <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100">Procurement Terms & Acknowledgement</h4>
            <p className="mt-2 leading-5">
              By sending this RFQ, you confirm that the requested scope, quantity, and delivery timing have been reviewed and are accurate to the best of your knowledge. You also understand that sending an RFQ is a supplier solicitation step only and does not yet create a binding purchase commitment.
            </p>
            <label className="mt-3 flex items-start gap-2 text-neutral-800 dark:text-neutral-100">
              <input
                type="checkbox"
                checked={createForm.termsAcknowledged}
                onChange={(e) => setCreateForm((f) => ({ ...f, termsAcknowledged: e.target.checked }))}
                className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-neutral-900 focus:ring-neutral-400 dark:border-neutral-600 dark:bg-neutral-900"
              />
              <span>I confirm that this RFQ has been reviewed and I agree to the procurement submission terms.</span>
            </label>
          </div>

          {selectedSupplierIds.length > 0 && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300">
              <strong>Ready to send.</strong> This RFQ will be sent to {selectedSupplierIds.length} supplier{selectedSupplierIds.length > 1 ? "s" : ""}.
              Each selected supplier will receive a notification and can submit their quote via the Supplier Portal.
            </div>
          )}
        </div>
      </FormModal>

      {/* ── Invite Supplier Modal ── */}
      <FormModal
        open={!!showInvite}
        onClose={closeInviteModal}
        title={`Invite Supplier to ${showInvite ? getRFQLabel(data.find((row) => String(row.id) === String(showInvite)) || (detail && String(detail.id) === String(showInvite) ? detail : { id: showInvite })) : "RFQ"}`}
        size="sm"
        footer={
          <>
            <BtnSecondary onClick={closeInviteModal}>Cancel</BtnSecondary>
            <BtnPrimary onClick={handleInvite} disabled={!inviteSupplierId}>Invite</BtnPrimary>
          </>
        }
      >
        <div>
          <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Select Supplier</label>
          <ShopSelect
            value={inviteSupplierId}
            onValueChange={setInviteSupplierId}
            options={[
              { value: "", label: "— Choose a supplier —" },
              ...availableInviteSuppliers.map((supplier) => ({ value: supplier.id, label: `${supplier.name || supplier.contact_name} ${supplier.contact_email ? `(${supplier.contact_email})` : ""}${supplier.portal_status === "REGISTERED" ? " ✓ Portal" : ""}` })),
            ]}
            ariaLabel="Select Supplier"
            align="start"
            minPanelWidth={260}
            triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm"
            panelClassName="py-1"
          />
          {allSuppliers.length === 0 && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">No suppliers found. Register suppliers first.</p>
          )}
          {allSuppliers.length > 0 && availableInviteSuppliers.length === 0 && (
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              All currently loaded suppliers have already been invited to this RFQ.
            </p>
          )}
          <p className="mt-2 text-[10px] text-neutral-400">
            Suppliers with portal access (✓) will see this RFQ immediately. Others will need to be contacted manually.
          </p>
        </div>
      </FormModal>

      {/* ── RFQ Detail + Quote Comparison Modal ── */}
      <FormModal
        open={!!detail || detailLoading}
        onClose={() => setDetail(null)}
        title={detail ? `${getRFQLabel(detail)} - ${getRFQDisplayTitle(detail)}` : "Loading..."}
        size="xl"
        {...{
          title: detail ? `${getRFQLabel(detail)} - ${getRFQDisplayTitle(detail)}` : "Loading...",
        }}
        footer={<BtnSecondary onClick={() => setDetail(null)}>Close</BtnSecondary>}
      >
        {detailLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-neutral-100 dark:bg-neutral-800" />)}
          </div>
        )}
        {detail && (
          <div className="space-y-6">
            <div className="space-y-2 rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 dark:border-neutral-700 dark:bg-neutral-900/40">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">
                Quote Comparison
              </p>
              <div className="flex flex-col gap-1 lg:flex-row lg:items-end lg:justify-between">
                <div className="min-w-0">
                  <h3 className="truncate text-lg font-semibold text-neutral-900 dark:text-neutral-50">
                    {getRFQDisplayTitle(detail)}
                  </h3>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    Review line-level pricing and supplier notes before accepting a quote.
                  </p>
                </div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400">
                  {getRFQLabel(detail)}
                </div>
              </div>
            </div>

            {/* RFQ Info */}
            <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
              <div>
                <span className="text-xs text-neutral-500 dark:text-neutral-400">Status</span>
                <p><StatusBadge status={detail.status} /></p>
              </div>
              <div>
                <span className="text-xs text-neutral-500 dark:text-neutral-400">Type</span>
                <p><StatusBadge status={detail.rfq_type || "STANDARD"} /></p>
              </div>
              <div>
                <span className="text-xs text-neutral-500 dark:text-neutral-400">Product</span>
                <p className="font-medium dark:text-neutral-100">{detail.product_name || "—"}</p>
              </div>
              <div>
                <span className="text-xs text-neutral-500 dark:text-neutral-400">Qty Needed</span>
                <p className="font-medium dark:text-neutral-100">{detail.requested_quantity ?? "—"}</p>
              </div>
              <div>
                <span className="text-xs text-neutral-500 dark:text-neutral-400">Source PR</span>
                <p className="font-medium dark:text-neutral-100">{getPRLabel(detail)}</p>
              </div>
              <div>
                <span className="text-xs text-neutral-500 dark:text-neutral-400">Deadline</span>
                <p className="font-medium dark:text-neutral-100">{detail.deadline_at ? new Date(detail.deadline_at).toLocaleDateString() : "—"}</p>
              </div>
              <div>
                <span className="text-xs text-neutral-500 dark:text-neutral-400">Created</span>
                <p className="font-medium dark:text-neutral-100">{new Date(detail.created_at).toLocaleDateString()}</p>
              </div>
            </div>

            <NoteCard label="RFQ note" value={detail.notes} tone="subtle" />

            {/* Invited Suppliers */}
            {detail.suppliers?.length > 0 && (
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                    Invited Suppliers ({detail.suppliers.length})
                  </h4>
                  {canInviteMoreSuppliers(detail.status) && (
                    <BtnSecondary onClick={() => openInviteModal(detail.id, { returnToDetail: true })}>
                      <Send className="h-4 w-4" />
                      Invite More Suppliers
                    </BtnSecondary>
                  )}
                </div>
                <div className="space-y-1.5">
                  {detail.suppliers.map((s) => (
                    <div key={s.supplier_id} className="flex items-center justify-between rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-800/50">
                      <span className="text-sm font-medium dark:text-neutral-100">{s.supplier_name || `Supplier #${s.supplier_id}`}</span>
                      <StatusBadge status={s.status} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.lines?.length > 0 && (
              <div>
                <div className="flex items-center justify-between gap-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                    Requested RFQ Lines ({detail.lines.length})
                  </h4>
                  <p className="text-[11px] text-neutral-400 dark:text-neutral-500">
                    Review the requested line items before accepting any supplier quote.
                  </p>
                </div>
                <div className="mt-2 max-h-80 overflow-auto rounded-xl border border-neutral-200 dark:border-neutral-700">
                  <table className="min-w-full divide-y divide-neutral-200 text-sm dark:divide-neutral-700">
                    <thead className="bg-neutral-50 dark:bg-neutral-800/80">
                      <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                        <th className="px-3 py-2">Line</th>
                        <th className="px-3 py-2">Variant</th>
                        <th className="px-3 py-2">Requested Qty</th>
                        <th className="px-3 py-2">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200 bg-white dark:divide-neutral-700 dark:bg-neutral-900/30">
                      {detail.lines.map((line) => (
                        <tr key={line.id || `${line.line_no}-${line.variant_id || line.variantId || "item"}`}>
                          <td className="px-3 py-2 font-medium text-neutral-700 dark:text-neutral-200">
                            #{getDisplayText(line.line_no || line.lineNo)}
                          </td>
                          <td className="px-3 py-2 text-neutral-700 dark:text-neutral-200">
                            <div className="font-medium">{getLineLabel(line, "Requested item")}</div>
                            {(line.variantSku || line.variant_sku) && (
                              <div className="text-[11px] text-neutral-500 dark:text-neutral-400">
                                SKU: {getDisplayText(line.variantSku || line.variant_sku)}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-neutral-700 dark:text-neutral-200">
                            {getDisplayText(line.requestedQuantity ?? line.requested_quantity)}
                          </td>
                          <td className="max-w-xs whitespace-pre-wrap break-words px-3 py-2 text-neutral-600 dark:text-neutral-300">
                            {getDisplayText(line.notes)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Waiting for supplier quotes info */}
            {canInviteMoreSuppliers(detail.status) && (!detail.quotes || detail.quotes.length === 0) && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/30">
                <div className="flex items-start gap-2">
                  <ExternalLink className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
                  <div className="text-xs text-blue-700 dark:text-blue-300">
                    <strong>Waiting for supplier quotes.</strong> Invited suppliers with portal access can view this RFQ and submit their quotes via the Supplier Portal. You can also manually invite more suppliers.
                    <div className="mt-3">
                      <BtnSecondary onClick={() => openInviteModal(detail.id, { returnToDetail: true })}>
                        <Send className="h-4 w-4" />
                        Invite Another Supplier
                      </BtnSecondary>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quote Comparison */}
            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                  Supplier Quotes ({detail.quotes?.length || 0})
                </h4>
                <p className="text-[11px] text-neutral-400 dark:text-neutral-500">
                  Accepted purchase orders are created from the quote lines shown below.
                </p>
              </div>

              {(!detail.quotes || detail.quotes.length === 0) ? (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">No quotes submitted yet.</p>
              ) : (
                <div className="space-y-2">
                  {detail.quotes.map((q) => {
                    const lineCount = getQuoteLineCount(q);
                    const linesLoaded = hasVisibleQuoteLineBreakdown(q);
                    const quoteLines = getQuoteLines(q);
                    const canAccept = q.status === "SUBMITTED" && detail.status !== "CLOSED" && linesLoaded;

                    return (
                    <div
                      key={q.id}
                      className={[
                        "overflow-hidden rounded-2xl border p-4 sm:p-5",
                        linesLoaded
                          ? "border-neutral-200 bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800/50"
                          : "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30",
                      ].join(" ")}
                    >
                      <div className="space-y-4">
                        <div className="min-w-0 space-y-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold dark:text-neutral-100">
                              {getDisplayText(q.supplier_name, `Supplier #${q.supplier_id}`)}
                            </span>
                            {q.quote_number && (
                              <span className="font-mono text-xs text-neutral-400 dark:text-neutral-500">
                                {getDisplayText(q.quote_number)}
                              </span>
                            )}
                            <StatusBadge status={q.status} />
                          </div>
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-neutral-600 dark:text-neutral-300">
                            <span>Unit Price: <strong>{fmtPeso(getQuoteDisplayUnitPriceCents(q))}</strong></span>
                            <span>Qty: <strong>{getQuoteDisplayQuantity(q)}</strong></span>
                            <span>Total: <strong>{fmtPeso(getQuoteDisplayTotalCents(q))}</strong></span>
                            <span>Delivery: <strong>{q.delivery_days ? `${q.delivery_days} days` : "—"}</strong></span>
                            {lineCount > 0 && <span>Lines: <strong>{lineCount}</strong></span>}
                          </div>
                          <NoteCard label="Main supplier note" value={q.notes} tone="accent" />

                          {lineCount > 0 && !linesLoaded && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200">
                              This quote includes line-level pricing, but the line breakdown is unavailable right now. Refresh the RFQ details before accepting it.
                            </div>
                          )}

                          {quoteLines.length > 0 && (
                            <div className="max-h-[28rem] overflow-auto rounded-xl border border-neutral-200 dark:border-neutral-700">
                              <table className="min-w-full divide-y divide-neutral-200 text-sm dark:divide-neutral-700">
                                <thead className="bg-white dark:bg-neutral-900/60">
                                  <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                                    <th className="px-3 py-2">Line</th>
                                    <th className="px-3 py-2">Variant</th>
                                    <th className="px-3 py-2">Requested</th>
                                    <th className="px-3 py-2">Quoted</th>
                                    <th className="px-3 py-2">Unit Price</th>
                                    <th className="px-3 py-2">Line Total</th>
                                    <th className="px-3 py-2">Availability</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-neutral-200 bg-white dark:divide-neutral-700 dark:bg-neutral-900/30">
                                  {quoteLines.map((line) => (
                                    <tr key={line.id || `${q.id}-${line.rfqLineId || line.rfq_line_id || line.line_no}`}>
                                      <td className="px-3 py-2 font-medium text-neutral-700 dark:text-neutral-200">
                                        #{line.line_no || line.lineNo || "—"}
                                      </td>
                                      <td className="px-3 py-2 text-neutral-700 dark:text-neutral-200">
                                        <div className="font-medium">{getLineLabel(line)}</div>
                                        {(line.variantSku || line.variant_sku) && (
                                          <div className="text-[11px] text-neutral-500 dark:text-neutral-400">
                                            SKU: {getDisplayText(line.variantSku || line.variant_sku)}
                                          </div>
                                        )}
                                        {(hasDisplayText(line.fulfillmentNote || line.fulfillment_note) || hasDisplayText(line.supplierNote || line.supplier_note)) && (
                                          <div className="mt-2 space-y-2">
                                            <NoteCard
                                              label="Fulfillment note"
                                              value={line.fulfillmentNote || line.fulfillment_note}
                                              tone="subtle"
                                              compact
                                            />
                                            <NoteCard
                                              label="Supplier line note"
                                              value={line.supplierNote || line.supplier_note}
                                              tone="accent"
                                              compact
                                            />
                                          </div>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-neutral-700 dark:text-neutral-200">
                                        {line.requestedQuantity ?? line.requested_quantity ?? "—"}
                                      </td>
                                      <td className="px-3 py-2 text-neutral-700 dark:text-neutral-200">
                                        {line.quotedQuantity ?? line.quoted_quantity ?? "—"}
                                      </td>
                                      <td className="px-3 py-2 text-neutral-700 dark:text-neutral-200">
                                        {fmtPeso(line.quotedUnitPriceCents ?? line.quoted_unit_price_cents ?? 0)}
                                      </td>
                                      <td className="px-3 py-2 text-neutral-700 dark:text-neutral-200">
                                        {fmtPeso(line.lineTotalCents ?? line.line_total_cents ?? 0)}
                                      </td>
                                      <td className="px-3 py-2 text-neutral-600 dark:text-neutral-300">
                                        {getSafeAvailabilityLabel(line)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                        {q.status === "SUBMITTED" && detail.status !== "CLOSED" && (
                          <div className="flex flex-col gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-700 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-xs text-neutral-500 dark:text-neutral-400">
                              Accepting this quote will award the RFQ and create a purchase order from the quoted line items shown above.
                            </p>
                            <button
                              onClick={() => handleAcceptQuote(detail.id, q)}
                              disabled={!canAccept}
                              className="business-primary-action inline-flex self-start items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400/60 disabled:text-white/90"
                            >
                              <CheckCircle className="h-4 w-4" /> Accept Quote
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </FormModal>
    </div>
  );
}
